import { useEffect, useState } from "react";
import {
  Phone,
  Database,
  CalendarCheck,
  Repeat,
  BarChart3,
  MessagesSquare,
  Workflow,
  GitBranch,
} from "lucide-react";

const NODES = [
  { key: "receptionist", label: "AI Receptionist", icon: Phone, x: 50, y: 8 },
  { key: "crm", label: "CRM", icon: Database, x: 12, y: 32 },
  { key: "calendar", label: "Calendar", icon: CalendarCheck, x: 88, y: 32 },
  { key: "followup", label: "Follow-Up", icon: Repeat, x: 12, y: 68 },
  { key: "comms", label: "Communication", icon: MessagesSquare, x: 88, y: 68 },
  { key: "workflow", label: "Workflow Nodes", icon: Workflow, x: 32, y: 92 },
  { key: "pipeline", label: "Lead Pipeline", icon: GitBranch, x: 68, y: 92 },
  { key: "analytics", label: "Analytics", icon: BarChart3, x: 50, y: 50 },
];

const EDGES = [
  ["receptionist", "analytics"],
  ["crm", "analytics"],
  ["calendar", "analytics"],
  ["followup", "analytics"],
  ["comms", "analytics"],
  ["workflow", "analytics"],
  ["pipeline", "analytics"],
  ["receptionist", "crm"],
  ["receptionist", "calendar"],
  ["crm", "followup"],
  ["calendar", "comms"],
  ["workflow", "pipeline"],
];

const PANELS = [
  "New lead captured",
  "Appointment booked",
  "Follow-up completed",
  "AI answered call",
  "Lead qualified",
];

const pos = (key) => NODES.find((n) => n.key === key);

export default function HeroDiagram() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((v) => (v + 1) % PANELS.length), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="hero-diagram" className="relative">
      <div className="glass relative overflow-hidden rounded-xl p-5 sm:p-8">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[10px] uppercase tracking-[0.24em] text-elv-dim">
            Elvoria AI — Platform Map
          </span>
          <span className="rounded-sm border border-elv-cyan/25 bg-elv-cyan/5 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-elv-cyan">
            Demo UI
          </span>
        </div>

        <div className="relative mt-6 h-[380px] w-full sm:h-[440px]">
          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {EDGES.map(([a, b], i) => {
              const from = pos(a);
              const to = pos(b);
              return (
                <line
                  key={`${a}-${b}`}
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke={i % 3 === 0 ? "rgba(0,255,255,0.35)" : "rgba(0,255,102,0.32)"}
                  strokeWidth="0.22"
                  strokeDasharray="2 2.5"
                  style={{ animation: `dash-flow ${9 + (i % 5) * 2}s linear infinite` }}
                />
              );
            })}
          </svg>

          {NODES.map((node, i) => {
            const Icon = node.icon;
            const isCore = node.key === "analytics";
            return (
              <div
                key={node.key}
                data-testid={`hero-node-${node.key}`}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${node.x}%`, top: `${node.y}%`, animationDelay: `${i * 90}ms` }}
              >
                <div
                  className={`glass glass-hover flex items-center gap-2 rounded-md px-2.5 py-2 sm:px-3 ${
                    isCore ? "border-elv-green/35 bg-elv-green/[0.07]" : ""
                  }`}
                >
                  <Icon className={`h-3.5 w-3.5 ${isCore ? "text-elv-green" : "text-elv-cyan"}`} />
                  <span className="whitespace-nowrap text-[10px] font-medium text-elv-text sm:text-[11px]">
                    {node.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 grid gap-2 border-t border-elv-line pt-5 sm:grid-cols-2">
          {PANELS.map((panel, i) => (
            <div
              key={panel}
              data-testid={`hero-panel-${i}`}
              className={`flex items-center justify-between rounded-md border px-3 py-2.5 text-[11px] transition-colors duration-500 ${
                i === active
                  ? "border-elv-green/35 bg-elv-green/[0.08] text-elv-text"
                  : "border-white/[0.06] bg-white/[0.02] text-elv-dim"
              }`}
            >
              <span className="flex items-center gap-2">
                <span
                  className={`h-1.5 w-1.5 rounded-full ${i === active ? "bg-elv-green" : "bg-white/20"}`}
                />
                {panel}
              </span>
              <span className="font-mono text-[10px] text-elv-dim">demo</span>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-3 text-center text-[11px] text-elv-dim">
        Illustrative interface. Panels above are demonstration elements, not live account activity.
      </p>
    </div>
  );
}
